import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";

export const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function errText(detail) {
  if (!detail) return "Request failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => `${(d.loc || []).slice(-1)[0] || "field"}: ${d.msg}`).join("; ");
  return detail.message || JSON.stringify(detail);
}

export class ApiError extends Error {
  constructor(status, detail) { super(errText(detail)); this.status = status; this.detail = detail; }
}

export function useApi() {
  const { getToken } = useAuth();
  return useCallback(async (method, path, body, opts = {}) => {
    const token = await getToken();
    const headers = { Authorization: `Bearer ${token}` };
    let payload;
    if (body instanceof FormData) payload = body;
    else if (body !== undefined) { headers["Content-Type"] = "application/json"; payload = JSON.stringify(body); }
    let res;
    try { res = await fetch(`${BACKEND}/api${path}`, { method, headers, body: payload }); }
    catch { throw new ApiError(0, "Network unavailable. Check your connection and retry."); }
    if (opts.raw) { if (!res.ok) throw new ApiError(res.status, "Download failed"); return res; }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new ApiError(res.status, data.detail);
    return data;
  }, [getToken]);
}

export function useResource(path, deps = []) {
  const api = useApi();
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const load = useCallback(async () => {
    if (!path) return;
    setState((s) => ({ ...s, loading: !s.data, error: null }));
    try { setState({ data: await api("GET", path), loading: false, error: null }); }
    catch (e) { setState({ data: null, loading: false, error: e }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, path, ...deps]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load, setData: (d) => setState((s) => ({ ...s, data: d })) };
}

export function usePoll(fn, active, ms = 2000) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => ref.current(), ms);
    return () => clearInterval(t);
  }, [active, ms]);
}

export const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random()).replace(/-/g, "");
